import React, { useEffect, useRef, useCallback } from 'react';

import './transactions.scss';
import { useDispatch, useSelector } from 'react-redux';
import { selectTransactionsPageContext } from '../../selectors/transactionsSelectors';
import { getTransactionsReset } from '../../actions/transactionActions';
import { DataTable, TableColumn } from '@ampol/design-system';
import { currencyFormat } from '../../utilities/core-utilities';
import { formatDate } from '../../utilities/dayjs-utilities';
import Validation from 'constants/validation-messages';
import ContentControls from '../../components/content-controls/content-controls';
import FilterControl from '../../components/filter-control/filter-control';
import Paginator from '../../components/pagination/pagination';
import TransactionsFilter from 'features/cards/components/transactions-filter/transactions-filter';
import { useTransactionsFilters } from 'hooks/useTransactionsFilters';
import { useTransactionsData } from 'hooks/useTransactionsData';
import { useTransactionsDownload } from 'hooks/useTransactionsDownload';
import { useFilterDisplay } from 'hooks/useFilterDisplay';
import type { ModalType, AccountLevel } from 'hooks/transactionTypes';

interface TransactionsProps {
  // eslint-disable-next-line no-unused-vars
  onItemSelected: (data: any) => void;
  accountId: string;
  cardNo: string;
  modalType?: ModalType;
  accountLevel?: AccountLevel;
}

const Transactions = ({ onItemSelected, accountId, cardNo, modalType, accountLevel }: TransactionsProps) => {
  const dispatch = useDispatch();

  const {
    transactions: transactionResults,
    transactionsTotalSize: transactionResultsSize,
    transactionsLoading: isTransactionsLoading,
    transactionsLoaded: isTransactionsLoaded,
    transactionsError: isTransactionsError,
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
  } = useSelector(selectTransactionsPageContext);

  const isMounted = useRef(false);
  const filterApplied = true; // Set to always true since Date is required

  // Custom hooks for managing state and business logic
  const { filterDisplay, setFilterDisplay, toggleFilter } = useFilterDisplay({ modalType });

  const { filterValues, setFilterValues, handlePageChange, handleColumnSort, resetFilterValues, handleSubmitFilter } =
    useTransactionsFilters({
      accountId,
      cardNo,
      modalType,
      loggedInUser,
      assignedAccountNumber: assignedAccountNumber as string,
    });

  const { getTransactionsPayload, getTransactionList } = useTransactionsData({
    accountId,
    cardNo,
    assignedAccountNumber: assignedAccountNumber as string,
    assignedGrandfatherClientNumber: assignedGrandfatherClientNumber as string,
    modalType,
    accountLevel,
  });

  const { handleDownload } = useTransactionsDownload({
    modalType,
    loggedInUser,
    assignedAccountNumber: assignedAccountNumber as string,
    getTransactionsPayload,
  });

  // Effect: Reset transactions when assigned account changes
  useEffect(() => {
    if (assignedAccountNumber && isMounted.current) {
      dispatch(getTransactionsReset());

      // Trigger filter if in a modal
      setFilterDisplay(!modalType);
      resetFilterValues(!!modalType, !!modalType, false);
    }
    // resetFilterValues and setFilterDisplay are intentionally excluded from deps.
    // Both are useCallback-memoised but depend on accountId/cardNo/modalType, so their
    // identity changes when those props change — which would cause spurious resets
    // unrelated to an assignedAccountNumber change.  Using refs to call the latest
    // version without making them reactive on this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedAccountNumber, dispatch, modalType]);

  // Effect: Fetch transactions when filter is triggered
  useEffect(() => {
    if (filterValues?.triggerFilter) {
      getTransactionList(filterValues, setFilterValues);
    }
  }, [filterValues, getTransactionList, setFilterValues]);

  // Effect: Mount/unmount cleanup
  useEffect(() => {
    isMounted.current = true;

    return () => {
      dispatch(getTransactionsReset());
    };
  }, [dispatch]);

  const getDataTableColumns = (): TableColumn[] => {
    return [
      modalType !== 'account' && {
        header: 'Account No',
        accessorKey: 'parentContractNumber',
        id: 'ParentContractNumber',
        defaultSorted: 'asc',
      },
      {
        header: 'Card No',
        accessorKey: 'cardNumber',
      },
      {
        header: 'Vehicle Registration',
        accessorKey: 'registrationNumber',
      },
      {
        header: 'Driver Name',
        accessorKey: 'driverName',
      },
      {
        header: 'Odometer',
        accessorKey: 'odometer',
      },
      {
        header: 'Transaction Date',
        id: 'transactionDate',
        accessorFn: (x: any) => formatDate(x.transactionDate, 'DD/MM/YYYY HH:mm:ss'),
      },
      {
        header: 'Amount',
        id: 'transactionAmount',
        accessorFn: (x: any) => currencyFormat(x.transactionAmount),
        rtl: true,
      },
    ].filter(Boolean) as TableColumn[];
  };

  const getNoDataMessage = () => {
    if (isTransactionsLoaded || (modalType === 'account' && accountLevel !== 'C')) {
      return Validation.NO_CONTENT;
    } else if (isTransactionsError) {
      return Validation.CONTENT_ERROR;
    } else {
      return Validation.FILTER_TRANSACTIONS;
    }
  };

  // Stable callbacks — prevents TransactionsFilter and ContentControls from re-rendering
  // every time filterValues changes, which would happen with inline arrow functions.
  const handleDownloadClick = useCallback(() => {
    handleDownload(filterValues);
  }, [handleDownload, filterValues]);

  const handleFilterSubmit = useCallback(
    (data: any) => {
      handleSubmitFilter(data);
      toggleFilter();
    },
    [handleSubmitFilter, toggleFilter]
  );

  return (
    <>
      <FilterControl
        showFilterControl={true}
        filterToggle={toggleFilter}
        filterDisplay={filterDisplay}
        filterApplied={filterApplied}
      >
        <div className='transactions-filter-zone' data-testid='transactions-filter-zone'>
          <TransactionsFilter
            modalType={modalType as any}
            filterValues={filterValues as any}
            setFilterValues={setFilterValues as any}
            onSubmit={handleFilterSubmit}
            resetFilterValues={resetFilterValues as any}
            accountLevel={accountLevel as any}
          />
        </div>
      </FilterControl>

      <ContentControls
        showPageControls={true}
        showFilterControl={false}
        selectControl={false}
        handleDownload={handleDownloadClick}
        filterToggle={toggleFilter}
        filterDisplay={filterDisplay}
        filterApplied={filterApplied}
        itemCount={transactionResultsSize}
        pageNumber={filterValues?.pageNumber}
        onPageChange={handlePageChange as any}
        totalRecords={transactionResults?.length}
      />
      <div className={'transaction-table-container'}>
        <DataTable
          caption={'Transactions'}
          columns={getDataTableColumns()}
          data={transactionResults}
          onRowClick={onItemSelected}
          onColumnSorted={handleColumnSort as any}
          tableVariant={'list'}
          isLoading={isTransactionsLoading}
          noDataMessage={getNoDataMessage()}
        />
      </div>
      <div className='mobile-paginator-control'>
        <Paginator
          itemCount={transactionResultsSize}
          pageNumber={filterValues?.pageNumber}
          onPageChange={handlePageChange as any}
          name={'transactions'}
        />
      </div>
    </>
  );
};

export default Transactions;
